import React, { useEffect, useState } from "react";
import { MenuItem, Select, SxProps, Theme } from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import { LANGUAGES, getCurrentLang } from "../utils/googleTranslate";
import {
  CONTENT_LANGUAGE_AUTO,
  getContentLanguage,
  getContentLanguageSetting,
  setContentLanguageSetting,
} from "../utils/contentLanguage";

/**
 * Language AI content is generated in.
 *
 *  mode="global"  (dashboard, next to Refresh): the saved setting for all generation.
 *                 Default "Same as site" = the language picked in the top-right menu.
 *  mode="local"   (customize dialogs): language for this one generation only;
 *                 starts with the current content language. Use `value` / `onChange`.
 */
type Props = {
  mode?: "global" | "local";
  value?: string;
  onChange?: (code: string) => void;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
  label?: string;
};

const labelOf = (code: string) => LANGUAGES.find((l) => l.code === code)?.label || "English";

const ContentLanguageSelect: React.FC<Props> = ({ mode = "global", value, onChange, size = "small", sx, label }) => {
  const [setting, setSetting] = useState<string>(getContentLanguageSetting());
  const [siteLang, setSiteLang] = useState<string>(getCurrentLang());

  useEffect(() => {
    // keep in sync when the site language / setting changes elsewhere
    const sync = () => { setSetting(getContentLanguageSetting()); setSiteLang(getCurrentLang()); };
    window.addEventListener("content-language-changed", sync);
    window.addEventListener("site-language-changed", sync);
    return () => {
      window.removeEventListener("content-language-changed", sync);
      window.removeEventListener("site-language-changed", sync);
    };
  }, []);

  const current = mode === "global" ? setting : (value || getContentLanguage());

  const handleChange = (v: string) => {
    if (mode === "global") {
      setContentLanguageSetting(v);       // saved + dashboard regenerates suggestions
      setSetting(v);
    }
    onChange?.(v);
  };

  return (
    <Select
      size={size}
      value={current}
      onChange={(e) => handleChange(String(e.target.value))}
      className="notranslate"
      title={label || "Language of generated content"}
      renderValue={(v) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <TranslateIcon sx={{ fontSize: 16, opacity: 0.8 }} />
          {v === CONTENT_LANGUAGE_AUTO ? labelOf(siteLang) : labelOf(String(v))}
        </span>
      )}
      MenuProps={{ PaperProps: { className: "notranslate", sx: { background: "#18181E", color: "#fff", maxHeight: 360 } } }}
      sx={{
        minWidth: 150, height: size === "small" ? 36 : undefined,
        color: "#fff", fontSize: 13, borderRadius: "10px", background: "rgba(255,255,255,0.06)",
        ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(167,139,250,0.6)" },
        ".MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
        ...(sx as any),
      }}
    >
      {mode === "global" && (
        <MenuItem value={CONTENT_LANGUAGE_AUTO} sx={{ fontSize: 13 }}>
          Same as site ({labelOf(siteLang)})
        </MenuItem>
      )}
      {LANGUAGES.map((l) => (
        <MenuItem key={l.code} value={l.code} sx={{ fontSize: 13 }}>{l.label}</MenuItem>
      ))}
    </Select>
  );
};

export default ContentLanguageSelect;
